/**
 * This method moves item from fromIndex to toIndex in array
 *
 * @param array array to modify
 * @param fromIndex current index of item
 * @param toIndex desired index of item
 */
export function moveItemToIndex<T>(array: T[], fromIndex: number, toIndex: number) {
  const itemToMove = array[fromIndex];

  array.splice(fromIndex, 1);
  array.splice(toIndex, 0, itemToMove);

  return array;
}

type InsertItemsInOrderParams<T extends { id: string }> = {
  array: T[];
  itemIds: string[];
  itemsById: Record<string, T>;
  /** Index in the original `array` (before any moved items are removed) to insert the block before. */
  insertBeforeIndex: number;
};

/**
 * Reorders or inserts multiple items into `array`, preserving `itemIds` order as
 * a single contiguous block. The block lands before the item at
 * `insertBeforeIndex` relative to the non-moving items, so moved items sitting
 * earlier in the array don't skew the final position (they're excluded from the
 * prefix count).
 */
export function insertItemsInOrder<T extends { id: string }>({
  array,
  itemIds,
  itemsById,
  insertBeforeIndex,
}: InsertItemsInOrderParams<T>) {
  const orderedItems: T[] = [];

  for (const itemId of itemIds) {
    const item = itemsById[itemId];

    if (item) {
      orderedItems.push(item);
    }
  }

  if (!orderedItems.length) {
    return;
  }

  const movingIds = new Set(orderedItems.map(({ id }) => id));
  const remaining = array.filter((entry) => !movingIds.has(entry.id));
  const insertIndex = array.slice(0, Math.max(insertBeforeIndex, 0)).filter((entry) => !movingIds.has(entry.id)).length;

  remaining.splice(insertIndex, 0, ...orderedItems);

  array.length = 0;
  array.push(...remaining);
}
